import {
  Box,
  Container,
  Heading,
  Link,
  RawUiKitProvider,
} from '@joinrepublic/dls-ui-kit';
import { SuccessfulNativeLogin } from '@ory/kratos-client';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { usePostLoginOnboardingRedirect } from '@r-client/republic/feature/account-setup-us';
import { MultiStepLayout } from '@r-client/republic/feature/multi-step-layout';
import { republicLegacy } from '@r-client/republic/ui/theme';
import {
  hasGlobalAuthSession,
  isGlobalAuth,
  useAuth,
  useGlobalAuth,
} from '@r-client/shared/data/auth';
import { useReporting } from '@r-client/shared/data/error-reporting';
import {
  getAuthPageProps,
  IJurisdictionAccounts,
  InvestmentBanner,
  LoginForm,
  resolveJurisdictionCookie,
  SessionPersistError,
  validateRedirectUrl,
} from '@r-client/shared/feature/global-auth';
import { E_REGION } from '@r-client/shared/feature/region';
import {
  browserNavigation,
  getCookie,
  setCookie,
  useAppConfig,
} from '@r-client/shared/util/core';

import { createGqlClient } from '../../../gql-client';

const GlobalLogin = observer(function GlobalLogin({
  initialRegion,
  bannerData,
  regionalRedirects,
  isSignedInGlobal,
  forwardedQueryString,
  loginChallenge,
  flowId,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { republicEuropeBaseUrl, publicTld } = useAppConfig();
  const { reloadSession } = useAuth();
  const globalAuth = useGlobalAuth();
  const reporting = useReporting();
  const router = useRouter();
  const redirectTo = regionalRedirects[initialRegion];
  const getOnboardingRedirect = usePostLoginOnboardingRedirect();

  const onSuccessfulLogin = async (
    returnTo?: string,
    accounts?: IJurisdictionAccounts,
    globalLogin?: SuccessfulNativeLogin,
    proxyResponseHeaders?: Record<string, string>
  ) => {
    reloadSession();

    if (accounts) {
      const currentJurisdiction = getCookie('jurisdiction');
      const newJurisdiction = resolveJurisdictionCookie(
        accounts,
        currentJurisdiction
      );
      if (newJurisdiction) {
        setCookie({
          name: 'jurisdiction',
          value: newJurisdiction,
          options: {
            maxAge: 400 * 24 * 60 * 60,
            sameSite: 'lax',
            domain: publicTld,
          },
        });
      }
    }

    const destination =
      returnTo ??
      validateRedirectUrl(redirectTo) ??
      (initialRegion === E_REGION.US ? '/' : republicEuropeBaseUrl ?? '/');

    if (globalLogin && isGlobalAuth(globalAuth)) {
      await globalAuth.reloadSession();

      if (globalAuth.errors?.response?.status === 401) {
        reporting?.error?.(
          'Post-login whoami returned 401 — session cookie likely not persisted',
          {
            hasIndicatorCookie: hasGlobalAuthSession(),
            region: initialRegion,
            finalDestination: destination,
            proxyResponseHeaders,
          }
        );
        throw new SessionPersistError();
      } else if (globalAuth.errors) {
        reporting?.error?.(
          'Post-login whoami failed with unexpected error — redirecting anyway',
          {
            status: globalAuth.errors.response?.status,
            error: globalAuth.errors.message,
            region: initialRegion,
            finalDestination: destination,
          }
        );
      }
    }

    // Whether onboarding should intercept the destination is decided by the
    // user's stored onboarding flow state, so this runs on every US login —
    // a plain login with no intent can still have onboarding to resume.
    let finalDestination = destination;
    if (initialRegion === E_REGION.US && globalLogin) {
      finalDestination =
        (await getOnboardingRedirect(destination)) ?? destination;
    }

    if (
      initialRegion === E_REGION.US &&
      globalLogin?.session.authenticator_assurance_level === 'aal1'
    ) {
      const mfaParams = new URLSearchParams({
        return_to: finalDestination,
        skip_setup: 'true',
      });
      return router.push(`/auth/mfa?${mfaParams}`);
    }

    // A full navigation, not router.push: `finalDestination` can be a route that only renders after
    // a server-side handoff (e.g. `/{slug}/invest`, routed through the monolith and back with
    // `invest_variant`). A client-side push skips that, so the page renders without its props.
    browserNavigation.assign(finalDestination);
  };

  const onRequiresVerification = (
    email: string,
    verificationFlowId: string
  ) => {
    const query: Record<string, string> = { email, flow: verificationFlowId };
    if (redirectTo) query.return_to = redirectTo;

    const params = new URLSearchParams(query);
    const verificationUrl = `/auth/verify-email?${params}`;
    router.push(verificationUrl);
  };

  // Use server-built query string so the link href is byte-identical between SSR
  // and hydration (avoids re-renders that can race the Turnstile Suspense boundary).
  const signUpUrl = forwardedQueryString
    ? `/auth/register?${forwardedQueryString}`
    : '/auth/register';

  const europeHref =
    initialRegion === E_REGION.EUROPE ? republicEuropeBaseUrl : undefined;

  return (
    <RawUiKitProvider theme={republicLegacy}>
      <Head>
        <title>Log in - Republic</title>
      </Head>
      <MultiStepLayout
        showStepsProgressBar={false}
        showFooter={false}
        logoHref={europeHref}
      >
        <Container
          maxWidth="470px"
          my="12"
          px={{ base: '5', md: '8' }}
          color="grey.900"
        >
          {bannerData && (
            <InvestmentBanner
              offeringName={bannerData.offeringName}
              imageUrl={bannerData.imageUrl}
              type={bannerData.type}
            />
          )}
          <Heading size="title-md" mb="0" mt="8">
            Log in
          </Heading>

          <Box
            mt="3"
            mb="8"
            color="grey.600"
            fontWeight="semibold"
            fontSize="md"
            letterSpacing="tight"
          >
            Don&apos;t have a Republic account?{' '}
            <Link
              display="inline-block"
              href={signUpUrl}
              size="body-md"
              color="blue.600"
              fontWeight="600"
              textDecoration="none"
              _hover={{ textDecoration: 'underline' }}
            >
              Sign up
            </Link>
          </Box>

          <LoginForm
            isSignedInGlobal={isSignedInGlobal}
            socialLoginRejected={router.query.error === 'social_no_account'}
            onSuccessfulLogin={onSuccessfulLogin}
            onRequiresVerification={onRequiresVerification}
            region={initialRegion}
            returnTo={redirectTo}
            loginChallenge={loginChallenge ?? undefined}
            flowId={flowId ?? undefined}
          />
        </Container>
      </MultiStepLayout>
    </RawUiKitProvider>
  );
});

export const getServerSideProps = getAuthPageProps(
  createGqlClient()
) satisfies GetServerSideProps;

export default GlobalLogin;
